import React, { useEffect, useMemo, useState } from 'react';

import projects from 'domains/projects';
import navigation from 'domains/navigation';

import { useAppDispatch, useAppSelector } from 'utils/hooks';

import Button from 'components/shared/Button';

import ProjectActionAlert from './ProjectActionAlert';
import ProjectActionTile from './ProjectActionTile';

import type { LoadProjectAlertType } from './LoadProjectModal';
import type { Project } from 'domains/projects';

function ProjectsList() {
  const dispatch = useAppDispatch();

  const status = useAppSelector(projects.selectors.status);
  const allProjects = useAppSelector(projects.selectors.selectAll);
  const restoreStatus = useAppSelector(projects.selectors.restoreStatus);

  const [showAlert, setShowAlert] = useState(false);
  const [alertType, setAlertType] = useState<LoadProjectAlertType>(null);
  const [activeProject, setActiveProject] = useState<Project | null>(null);
  const [lastFocusedElementId, setLastFocusedElementId] = useState('');

  const activeProjects = useMemo(() => allProjects.filter((p) => !p.last_ordered_at), [projects]);
  const orderedProjects = useMemo(() => allProjects.filter((p) => !!p.last_ordered_at), [projects]);

  function handleRestore(p: Project) {
    dispatch(projects.actions.restore.pending(p.guid));
  }

  function handleDelete(p: Project) {
    dispatch(projects.actions.destroy.pending(p));
  }

  function handleBackToCart() {
    dispatch(navigation.actions.navigateTo('cart'));
  }

  function handleOpenAlert(whichAlert: LoadProjectAlertType, project: Project) {
    setAlertType(whichAlert);
    setActiveProject(project);
    updateLastFocusedEl(whichAlert);
    setShowAlert(true);
  }

  function updateLastFocusedEl(whichAlert: LoadProjectAlertType) {
    switch (whichAlert) {
      case 'restore':
        setLastFocusedElementId('restore-project-button');
        break;
      case 'delete':
        setLastFocusedElementId('remove-project-button');
        break;
      default:
        setLastFocusedElementId('');
        break;
    }
  }

  function handleContinue() {
    setShowAlert(false);
    if (!activeProject) return;

    if (alertType === 'restore') {
      handleRestore(activeProject);
    }

    if (alertType === 'delete') {
      handleDelete(activeProject);
    }
  }

  function handleCancel() {
    setAlertType(null);
    setActiveProject(null);
    setShowAlert(false);
  }

  useEffect(() => {
    if (restoreStatus === 'complete') {
      handleBackToCart();
    }
  }, [restoreStatus]);

  return (
    <React.Fragment>
      <div className="projects-list-header">
        <h1>Projects</h1>
        <Button type="link" icon="close" onClick={handleBackToCart}>
          Back to Cart
        </Button>
      </div>

      <div className="projects-list">
        {status !== 'loading' && allProjects.length === 0 && (
          <p>You have 0 active projects. Start creating projects by adding items to your cart.</p>
        )}
        <ul>
          {activeProjects.map((project) => (
            <li key={project.id} aria-label={`project named ${project.name}`}>
              <ProjectActionTile
                project={project}
                appearance="dark"
                onDelete={(project) => handleOpenAlert('delete', project)}
                onRestore={(project) => handleOpenAlert('restore', project)}
              />
            </li>
          ))}
        </ul>
      </div>

      {orderedProjects.length > 0 && (
        <React.Fragment>
          <div className="projects-list-header">
            <h2>Previously Ordered Projects</h2>
          </div>
          <div className="projects-list">
            <ul>
              {orderedProjects.map((project) => (
                <li key={project.id} aria-label={`project named ${project.name}`}>
                  <ProjectActionTile
                    project={project}
                    appearance="dark"
                    onDelete={(project) => handleOpenAlert('delete', project)}
                    onRestore={(project) => handleOpenAlert('restore', project)}
                  />
                </li>
              ))}
            </ul>
          </div>
        </React.Fragment>
      )}

      {showAlert && alertType && (
        <ProjectActionAlert
          alertType={alertType}
          lastFocusedElementId={lastFocusedElementId}
          onCancel={handleCancel}
          onContinue={handleContinue}
          onClose={handleCancel}
        />
      )}
    </React.Fragment>
  );
}

export default ProjectsList;
